import React from "react";
import { useSubscription } from "@apollo/client";
import { ON_COMPANY_DATA_IMPORT_JOB_LOG_STREAM_SUBSCRIPTION } from "@/modules/stochastic/features/ImportStatus/graphql/subscriptions/onCompanyDataImportJobLogStreamSubscriptions/onCompanyDataImportJobLogStreamSubscriptions";
import { CompanyDataImportLogStreamSubscriptionData } from "@/modules/stochastic/features/ImportStatus/graphql/subscriptions/onCompanyDataImportJobLogStreamSubscriptions/types";

interface LogStreamModalProps {
  jobId: number;
  onClose: () => void;
}

const LogStreamModal: React.FC<LogStreamModalProps> = ({ jobId, onClose }) => {
  // GraphQL subscription
  const { data, loading, error } =
    useSubscription<CompanyDataImportLogStreamSubscriptionData>(
      ON_COMPANY_DATA_IMPORT_JOB_LOG_STREAM_SUBSCRIPTION,
      {
        variables: { jobId },
      },
    );

  const job = data?.company_data_import_job?.[0];
  const rowCount = job?.row_count ?? null;
  const errorCount = job?.error_count ?? null;
  const status = job?.status ?? null;

  // log_stream is a column value, so every subscription push carries the
  // whole log rather than a delta. Render what arrived; appending would
  // repeat the entire log on each update.
  const logStream = job?.log_stream ?? "";

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black opacity-50" onClick={onClose} />

      {/* Modal Container */}
      <div
        className="relative bg-white dark:bg-slate-800 p-6 rounded shadow-lg z-10
                      max-w-3xl max-h-[80vh] overflow-y-auto
                      w-full min-w-[500px]"
      >
        <h2 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">
          Log Stream
        </h2>

        {!loading && !error && (
          <div className="mb-4 flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600 dark:text-gray-400">
            {status && (
              <span>
                Status:{" "}
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {status}
                </span>
              </span>
            )}
            {rowCount !== null && (
              <span>
                Records:{" "}
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {rowCount.toLocaleString()}
                </span>
              </span>
            )}
            {errorCount !== null && (
              <span>
                Errors:{" "}
                <span
                  className={`font-medium ${
                    errorCount > 0
                      ? "text-red-600 dark:text-red-400"
                      : "text-gray-900 dark:text-gray-100"
                  }`}
                >
                  {errorCount.toLocaleString()}
                </span>
              </span>
            )}
          </div>
        )}

        {/* Show loading/error or the log content */}
        {loading && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Loading log...
          </p>
        )}
        {error && (
          <p className="text-sm text-red-600">
            Error fetching log: {error.message}
          </p>
        )}
        {!loading && !error && (
          <pre className="whitespace-pre-wrap break-words text-sm bg-gray-100 dark:bg-slate-700 dark:text-gray-300 p-4 rounded">
            {logStream || "(No logs yet)"}
          </pre>
        )}

        <div className="mt-4 flex space-x-2">
          <button
            className="px-3 py-1 rounded bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-900 dark:text-gray-100 text-sm"
            onClick={() => {
              navigator.clipboard
                .writeText(logStream)
                .then(() => alert("Log stream copied to clipboard!"))
                .catch(() => alert("Could not copy to clipboard."));
            }}
          >
            Copy Log
          </button>
          <button
            className="px-3 py-1 rounded bg-red-500 hover:bg-red-600 text-white text-sm"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default LogStreamModal;
